import React, { useState, useEffect } from 'react';
import { X, CheckCircle, User, Calendar, MapPin, Plus, Minus, Package, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ManualTrainingRegistration = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    user_id: '',
    date: '',
    location_id: '',
    activity: '',
    is_trial: false,  // Prøveskyting flag
    trial_name: '',    // Custom name for trial shooters
    registered_by: '', // Who registered this session
    registered_by_manual: '' // Manual name input
  });
  const [members, setMembers] = useState([]);
  const [rangeOfficers, setRangeOfficers] = useState([]); // Skyteledere and admins
  const [locations, setLocations] = useState([]);
  // State for adding a new custom personal location
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [savingLocation, setSavingLocation] = useState(false);
  const [activityTypes, setActivityTypes] = useState(['Organisert trening', 'Klubbstevne', 'Dugnad', 'NSF-øvelser']); // Default fallback
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [useManualOfficerName, setUseManualOfficerName] = useState(false);
  
  // Ammo registration state
  const [showAmmoSection, setShowAmmoSection] = useState(false);
  const [ammoTypes, setAmmoTypes] = useState([]);
  const [selectedAmmo, setSelectedAmmo] = useState({});
  const [loadingAmmo, setLoadingAmmo] = useState(false);

  // Load members and locations when component opens
  useEffect(() => {
    if (isOpen) {
      loadData();
      // Set default date to today
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, date: today }));
    }
  }, [isOpen, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // For regular members, only load their own data. For admins, load all members
      if (user?.role === 'member') {
        // Members can only register for themselves
        setMembers([user]);
        setFormData(prev => ({ ...prev, user_id: user.id }));
      } else {
        // Load organization members for admins
        const membersResponse = await axios.get(`${API_BASE}/api/users`, {
          params: { organization_id: user?.organization_id },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setMembers(membersResponse.data);
        
        // Filter out range officers and admins for the "Skyteleder" dropdown
        const officers = membersResponse.data.filter(m => 
          m.role === 'range_officer' || m.role === 'org_admin' || m.role === 'admin'
        );
        setRangeOfficers(officers);
      }

      // Load training locations
      const locationsResponse = await axios.get(
        `${API_BASE}/api/organizations/${user?.organization_id}/locations`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setLocations(locationsResponse.data);

      // Load organization activity types
      const activityTypesResponse = await axios.get(
        `${API_BASE}/api/organizations/${user?.organization_id}/activity-types`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const loadedActivityTypes = activityTypesResponse.data.activity_types;
      setActivityTypes(loadedActivityTypes);
      
      // Set default activity to first available type
      if (loadedActivityTypes.length > 0 && !formData.activity) {
        setFormData(prev => ({ ...prev, activity: loadedActivityTypes[0] }));
      }

    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Kunne ikke laste data. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  // Load ammo types when ammo section is opened
  const loadAmmoTypes = async () => {
    if (ammoTypes.length > 0) return; // Already loaded
    
    setLoadingAmmo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Only show enabled ammo types with stock
      const enabled = response.data.ammo_types?.filter(a => a.enabled && a.current_stock > 0) || [];
      setAmmoTypes(enabled);
      
      // Initialize selectedAmmo with 0 for all types
      const initial = {};
      enabled.forEach(ammo => {
        initial[ammo.caliber] = 0;
      });
      setSelectedAmmo(initial);
    } catch (error) {
      console.error('Failed to load ammo types:', error);
    } finally {
      setLoadingAmmo(false);
    }
  };

  // Handle ammo increment/decrement
  const handleAmmoIncrement = (caliber) => {
    const ammo = ammoTypes.find(a => a.caliber === caliber);
    if (!ammo) return;
    
    const current = selectedAmmo[caliber] || 0;
    if (current >= ammo.current_stock) return;
    
    setSelectedAmmo(prev => ({ ...prev, [caliber]: current + 1 }));
  };

  const handleAmmoDecrement = (caliber) => {
    const current = selectedAmmo[caliber] || 0;
    if (current > 0) {
      setSelectedAmmo(prev => ({ ...prev, [caliber]: current - 1 }));
    }
  };

  // Save a new personal custom training location (per-member)
  const handleAddCustomLocation = async () => {
    const name = newLocationName.trim();
    if (!name) {
      setError('Skriv inn navn på lokasjonen');
      return;
    }
    setSavingLocation(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/api/users/me/custom-locations`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newLoc = res.data;
      // Add to local list if not already present
      setLocations(prev => {
        const exists = prev.some(l => l.id === newLoc.id);
        return exists ? prev : [...prev, newLoc];
      });
      // Auto-select the newly added location
      setFormData(prev => ({ ...prev, location_id: newLoc.id }));
      setNewLocationName('');
      setShowAddLocation(false);
    } catch (err) {
      console.error('Failed to add custom location:', err);
      const msg = err.response?.data?.detail || 'Kunne ikke lagre lokasjonen';
      setError(typeof msg === 'string' ? msg : 'Kunne ikke lagre lokasjonen');
    } finally {
      setSavingLocation(false);
    }
  };

  // Get total ammo boxes selected
  const getTotalAmmoBoxes = () => {
    return Object.values(selectedAmmo).reduce((sum, qty) => sum + qty, 0);
  };

  // Determine ammo tag based on activity
  const getAmmoTag = () => {
    if (formData.is_trial) return 'GJESTE-SKYTING';
    const activity = formData.activity?.toLowerCase() || '';
    if (activity.includes('sikkerhet') || activity.includes('kurs')) return 'SIKKERH.KURS';
    if (activity.includes('innskyting')) return 'INNSKYTING';
    return 'MEDLEM';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError('');

    try {
      // Validate: if trial, require trial_name
      if (formData.is_trial && !formData.trial_name.trim()) {
        setError('Vennligst skriv inn navn for prøveskyteren');
        setSubmitLoading(false);
        return;
      }

      // Create FormData object since backend expects Form data, not JSON
      const formPayload = new FormData();
      
      // For trial shooting, don't require user_id
      if (!formData.is_trial) {
        formPayload.append('user_id', formData.user_id);
      }
      
      formPayload.append('location_id', formData.location_id);
      formPayload.append('date', formData.date);
      formPayload.append('start_time', '12:00');
      formPayload.append('activity', formData.activity);
      formPayload.append('is_trial', formData.is_trial ? 'true' : 'false');
      
      if (formData.is_trial && formData.trial_name.trim()) {
        formPayload.append('trial_name', formData.trial_name.trim());
      }
      
      // Add registered_by info
      if (useManualOfficerName && formData.registered_by_manual.trim()) {
        formPayload.append('registered_by_name', formData.registered_by_manual.trim());
      } else if (formData.registered_by) {
        formPayload.append('registered_by', formData.registered_by);
      }

      const response = await axios.post(
        `${API_BASE}/api/training/manual-register`,
        formPayload,
        {
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // If ammo was selected, register ammo sale
      const totalAmmo = getTotalAmmoBoxes();
      if (totalAmmo > 0) {
        try {
          const ammoTag = getAmmoTag();
          const memberName = formData.is_trial 
            ? formData.trial_name.trim() 
            : members.find(m => m.id === formData.user_id)?.full_name || 'Ukjent';
          
          const salesData = Object.entries(selectedAmmo)
            .filter(([_, qty]) => qty > 0)
            .map(([caliber, boxes_sold]) => ({ caliber, boxes_sold }));
          
          await axios.post(
            `${API_BASE}/api/admin/ammo-sale`,
            {
              member_id: formData.is_trial ? null : formData.user_id,
              member_name: memberName,
              session_id: response.data?.session?.id || null,
              sales: salesData,
              tags: [ammoTag],
              is_external: formData.is_trial
            },
            {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            }
          );
          setSuccess(`Treningsøkt og ${totalAmmo} ammo-esker registrert!`);
        } catch (ammoError) {
          console.error('Failed to register ammo:', ammoError);
          // Training was registered, but ammo failed
          setSuccess('Treningsøkt registrert! (Ammo-registrering feilet)');
        }
      } else {
        setSuccess('Treningsøkt registrert!');
      }
      
      setTimeout(() => {
        onSuccess && onSuccess(response.data);
        onClose();
        // Reset form
        const today = new Date().toISOString().split('T')[0];
        setFormData({
          user_id: '',
          date: today,
          location_id: '',
          activity: activityTypes[0] || 'Organisert trening',
          is_trial: false,
          trial_name: '',
          registered_by: '',
          registered_by_manual: ''
        });
        setUseManualOfficerName(false);
        setShowAmmoSection(false);
        setSelectedAmmo({});
        setSuccess('');
      }, 1500);

    } catch (error) {
      console.error('Registration failed:', error);
      // Ensure error is always a string
      let errorMessage = 'Kunne ikke registrere treningsøkt';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail.msg) {
          errorMessage = error.response.data.detail.msg;
        }
      }
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Registrer Manuell Treningsøkt</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Member Selection - Only for admins */}
            {user?.role !== 'member' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-300">
                    Medlem
                  </label>
                  {/* Prøveskyting checkbox */}
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_trial}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        is_trial: e.target.checked,
                        user_id: e.target.checked ? '' : prev.user_id  // Clear user_id when trial is checked
                      }))}
                      className="h-4 w-4 text-orange-500 focus:ring-orange-500 bg-gray-700 border-gray-600 rounded"
                    />
                    <span className="text-sm text-orange-400 font-medium">Gjeste-skyting</span>
                  </label>
                </div>
                
                {formData.is_trial ? (
                  /* Custom name input for trial shooters */
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-orange-400" />
                    <input
                      type="text"
                      name="trial_name"
                      value={formData.trial_name}
                      onChange={handleInputChange}
                      placeholder="Skriv inn navn på prøveskyter..."
                      className="w-full pl-10 pr-4 py-3 bg-orange-900/20 border-2 border-orange-500/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-orange-400 mt-1">
                      Denne registreringen vil merkes som gjeste-skyting
                    </p>
                  </div>
                ) : (
                  /* Regular member dropdown */
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleInputChange}
                      required={!formData.is_trial}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Velg medlem...</option>
                      {[...members].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb')).map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.full_name} ({member.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Skyteleder - who is supervising/registering */}
            {user?.role !== 'member' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Skyteleder (valgfritt)
                </label>
                
                {useManualOfficerName ? (
                  /* Manual name input */
                  <div className="space-y-2">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-400" />
                      <input
                        type="text"
                        name="registered_by_manual"
                        value={formData.registered_by_manual}
                        onChange={handleInputChange}
                        placeholder="Skriv navn på skyteleder..."
                        className="w-full pl-10 pr-4 py-3 bg-blue-900/20 border-2 border-blue-500/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setUseManualOfficerName(false);
                        setFormData(prev => ({ ...prev, registered_by_manual: '' }));
                      }}
                      className="text-sm text-blue-400 hover:text-blue-300"
                    >
                      ← Velg fra liste
                    </button>
                  </div>
                ) : (
                  /* Dropdown with officers */
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="registered_by"
                      value={formData.registered_by}
                      onChange={(e) => {
                        if (e.target.value === 'manual') {
                          setUseManualOfficerName(true);
                          setFormData(prev => ({ ...prev, registered_by: '' }));
                        } else {
                          handleInputChange(e);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Velg skyteleder (valgfritt)...</option>
                      <option value="manual" className="text-blue-400">✏️ Skriv navn manuelt</option>
                      {rangeOfficers.length > 0 && (
                        <optgroup label="Skyteledere og Admin">
                          {[...rangeOfficers].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb')).map((officer) => (
                            <option key={officer.id} value={officer.id}>
                              {officer.full_name} ({officer.role === 'admin' ? 'Admin' : 'Skyteleder'})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Hvem som var ansvarlig skyteleder under aktiviteten
                </p>
              </div>
            )}

            {/* Info text for regular members */}
            {user?.role === 'member' && (
              <div className="bg-blue-600/20 border border-blue-500/50 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  Du registrerer trening for: <strong>{user?.full_name}</strong>
                </p>
              </div>
            )}

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Dato
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>

            {/* Training Location */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Treningslokasjon
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  name="location_id"
                  value={formData.location_id}
                  onChange={(e) => {
                    if (e.target.value === '__add_new__') {
                      setShowAddLocation(true);
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  data-testid="manual-location-select"
                >
                  <option value="">Velg lokasjon...</option>
                  {locations.filter(l => !l.is_personal).length > 0 && (
                    <optgroup label="Klubbens lokasjoner">
                      {locations.filter(l => !l.is_personal).map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {locations.filter(l => l.is_personal).length > 0 && (
                    <optgroup label="Mine egne lokasjoner">
                      {locations.filter(l => l.is_personal).map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <option value="__add_new__">+ Legg til ny lokasjon...</option>
                </select>
              </div>

              {showAddLocation && (
                <div
                  className="mt-3 p-3 bg-gray-900/60 border border-yellow-700/40 rounded-lg"
                  data-testid="add-custom-location-box"
                >
                  <label className="block text-xs text-yellow-300 mb-2">
                    Nytt sted (f.eks. "Hamar SK – stevne"). Lagres som dine egne hurtigvalg.
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomLocation();
                        }
                      }}
                      placeholder="Navn på lokasjon"
                      autoFocus
                      maxLength={120}
                      disabled={savingLocation}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                      data-testid="add-custom-location-input"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleAddCustomLocation}
                        disabled={savingLocation || !newLocationName.trim()}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-medium rounded-lg transition-colors text-sm"
                        data-testid="add-custom-location-save"
                      >
                        <Save className="w-4 h-4" />
                        {savingLocation ? 'Lagrer...' : 'Lagre'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddLocation(false);
                          setNewLocationName('');
                          setError('');
                        }}
                        disabled={savingLocation}
                        className="px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Activity Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Aktivitet
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {activityTypes.map((activity) => (
                  <button
                    key={activity}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, activity }))}
                    className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                      formData.activity === activity
                        ? 'bg-yellow-500 text-gray-900'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {activity}
                  </button>
                ))}
              </div>
            </div>

            {/* Ammo Delivery Section - Only for admins/range officers */}
            {user?.role !== 'member' && (
              <div className="border-t border-gray-700 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAmmoSection(!showAmmoSection);
                    if (!showAmmoSection) {
                      loadAmmoTypes();
                    }
                  }}
                  className="flex items-center justify-between w-full p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-400" />
                    <span className="text-sm font-medium text-white">
                      Registrer ammo-utlevering
                    </span>
                    {getTotalAmmoBoxes() > 0 && (
                      <span className="px-2 py-0.5 bg-blue-500/30 text-blue-300 text-xs rounded-full">
                        {getTotalAmmoBoxes()} esker valgt
                      </span>
                    )}
                  </div>
                  <span className="text-gray-400 text-sm">{showAmmoSection ? '▲' : '▼'}</span>
                </button>
                
                {showAmmoSection && (
                  <div className="mt-3 p-3 bg-gray-700/30 rounded-lg space-y-3">
                    {loadingAmmo ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      </div>
                    ) : ammoTypes.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-2">
                        Ingen ammunisjon tilgjengelig på lager
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-gray-400">
                          Velg ammunisjon som skal utleveres. Kategoriseres automatisk som <span className="text-yellow-400 font-medium">{getAmmoTag()}</span>
                        </p>
                        <div className="space-y-2">
                          {ammoTypes.map((ammo) => (
                            <div
                              key={ammo.caliber}
                              className="flex items-center justify-between p-2 bg-gray-800 rounded"
                            >
                              <div className="flex-1">
                                <p className="text-white text-sm font-medium">{ammo.caliber}</p>
                                <p className="text-gray-400 text-xs">
                                  {ammo.current_stock} esker på lager
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleAmmoDecrement(ammo.caliber)}
                                  disabled={!selectedAmmo[ammo.caliber]}
                                  className="p-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded transition-colors"
                                >
                                  <Minus className="h-4 w-4 text-white" />
                                </button>
                                <span className="w-8 text-center text-white font-medium">
                                  {selectedAmmo[ammo.caliber] || 0}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleAmmoIncrement(ammo.caliber)}
                                  disabled={selectedAmmo[ammo.caliber] >= ammo.current_stock}
                                  className="p-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-30 rounded transition-colors"
                                >
                                  <Plus className="h-4 w-4 text-white" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Error/Success Messages */}
            {error && (
              <div className="bg-red-600/20 border border-red-600/50 rounded-md p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-600/20 border border-green-600/50 rounded-md p-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                  <p className="text-green-400 text-sm">{success}</p>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-4 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={submitLoading}
                className="flex-1 flex items-center justify-center py-3 px-4 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
              >
                <Plus className="h-5 w-5 mr-2" />
                {submitLoading ? 'Registrerer...' : 'Registrer Treningsøkt'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ManualTrainingRegistration;